import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// journeys is the Lifecycle journeys page (the design); settings is the
// engine and per-journey settings view that used to be the journeys tab.
export type LifecycleTab =
  | 'journeys'
  | 'call-tasks'
  | 'copy'
  | 'events'
  | 'settings';
export type JourneyKey = `J${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;

export type JourneysParams = {
  open: JourneyKey | null;
  window: '7d' | '30d';
};
export type CallTasksParams = {
  status: 'open' | 'completed' | 'skipped';
  journey: JourneyKey | null;
  mine: boolean;
  page: number;
  task: number | null;
};
export type CopyParams = {
  message: string | null;
  lang: 'en' | 'es';
  view: 'edit' | 'history';
};
export type EventsParams = {
  event_key: string | null;
  page: number;
};

export type LifecycleParamsByTab = {
  journeys: JourneysParams;
  settings: JourneysParams;
  'call-tasks': CallTasksParams;
  copy: CopyParams;
  events: EventsParams;
};

export type LifecycleUpdateOptions = { replace?: boolean };

export type LifecycleUrlState<T extends LifecycleTab = LifecycleTab> = {
  tab: LifecycleTab;
  params: LifecycleParamsByTab[T];
  setTab: (tab: LifecycleTab) => void;
  update: (
    partial: Partial<LifecycleParamsByTab[T]>,
    options?: LifecycleUpdateOptions,
  ) => void;
};

export const LIFECYCLE_TABS: LifecycleTab[] = [
  'journeys',
  'call-tasks',
  'copy',
  'events',
  'settings',
];

const DEFAULT_TAB: LifecycleTab = 'journeys';
const JOURNEY_KEY = /^J[1-9]$/;
const STEP_KEY = /^[a-z0-9_]+$/;

const TAB_KEYS: Record<LifecycleTab, string[]> = {
  journeys: ['open', 'window'],
  'call-tasks': ['status', 'journey', 'mine', 'page', 'task'],
  copy: ['message', 'lang', 'view'],
  events: ['event_key', 'page'],
  settings: ['open', 'window'],
};

const FILTER_KEYS = ['status', 'journey', 'mine', 'event_key'];

const oneOf = <V extends string>(
  value: string | null,
  allowed: readonly V[],
  fallback: V,
): V => (allowed.includes(value as V) ? (value as V) : fallback);

const journeyKey = (value: string | null): JourneyKey | null =>
  value && JOURNEY_KEY.test(value) ? (value as JourneyKey) : null;

const positiveInt = (value: string | null): number | null =>
  value && /^[1-9]\d*$/.test(value) ? Number(value) : null;

const parseTab = (value: string | null): LifecycleTab =>
  oneOf(value, LIFECYCLE_TABS, DEFAULT_TAB);

const parseParams = (
  tab: LifecycleTab,
  sp: URLSearchParams,
): LifecycleParamsByTab[LifecycleTab] => {
  switch (tab) {
    case 'call-tasks':
      return {
        status: oneOf(
          sp.get('status'),
          ['open', 'completed', 'skipped'] as const,
          'open',
        ),
        journey: journeyKey(sp.get('journey')),
        mine: sp.get('mine') === '1',
        page: positiveInt(sp.get('page')) ?? 1,
        task: positiveInt(sp.get('task')),
      };
    case 'copy': {
      const message = sp.get('message');
      return {
        message: message && STEP_KEY.test(message) ? message : null,
        lang: oneOf(sp.get('lang'), ['en', 'es'] as const, 'en'),
        view: oneOf(sp.get('view'), ['edit', 'history'] as const, 'edit'),
      };
    }
    case 'events':
      return {
        event_key: sp.get('event_key') || null,
        page: positiveInt(sp.get('page')) ?? 1,
      };
    default:
      return {
        open: journeyKey(sp.get('open')),
        window: oneOf(sp.get('window'), ['7d', '30d'] as const, '7d'),
      };
  }
};

const serialize = (value: unknown): string | null => {
  if (value === null || value === undefined || value === false) return null;
  if (value === true) return '1';
  return String(value);
};

const DEFAULTS: Record<string, string> = {
  window: '7d',
  status: 'open',
  lang: 'en',
  view: 'edit',
  page: '1',
};

export function useLifecycleUrlState<
  T extends LifecycleTab = LifecycleTab,
>(): LifecycleUrlState<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const search = searchParams.toString();

  const params = useMemo(
    () => parseParams(tab, new URLSearchParams(search)),
    [tab, search],
  );

  const latestSearch = useRef(search);
  latestSearch.current = search;

  const commit = useCallback(
    (next: URLSearchParams, replace: boolean) => {
      latestSearch.current = next.toString();
      setSearchParams(next, { replace });
    },
    [setSearchParams],
  );

  const setTab = useCallback(
    (nextTab: LifecycleTab) => {
      const current = parseTab(
        new URLSearchParams(latestSearch.current).get('tab'),
      );
      if (nextTab === current) return;
      const next = new URLSearchParams();
      if (nextTab !== DEFAULT_TAB) next.set('tab', nextTab);
      commit(next, false);
    },
    [commit],
  );

  const update = useCallback(
    (
      partial: Partial<LifecycleParamsByTab[T]>,
      options?: LifecycleUpdateOptions,
    ) => {
      const next = new URLSearchParams(latestSearch.current);
      const currentTab = parseTab(next.get('tab'));
      if (currentTab === DEFAULT_TAB) next.delete('tab');
      else next.set('tab', currentTab);
      const allowed = TAB_KEYS[currentTab];
      const entries = Object.entries(partial).filter(([key]) =>
        allowed.includes(key),
      );
      entries.forEach(([key, value]) => {
        const text = serialize(value);
        if (text === null || text === DEFAULTS[key]) next.delete(key);
        else next.set(key, text);
      });
      const keys = entries.map(([key]) => key);
      const resetsPage =
        keys.some((key) => FILTER_KEYS.includes(key)) && !keys.includes('page');
      if (resetsPage) next.delete('page');
      const changesPage = keys.includes('page');
      commit(next, options?.replace ?? !changesPage);
    },
    [commit],
  );

  return {
    tab,
    params: params as LifecycleParamsByTab[T],
    setTab,
    update,
  };
}
